import React from "react";
import { graphql } from "react-relay";

import { faCheck, faInfinity } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faArrowAltCircleUp,
	faBuilding,
	faBullseyeArrow,
	faCity,
	faCrown,
	faHeadphones,
	faSparkles,
	faThumbsUp,
	faUser,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addMonths, differenceInDays } from "date-fns";
import { fetchQuery } from "relay-runtime";

import analyticsImage from "~/legacy-assets/images/illustrations/AnalyticsPaywall.svg?url";
import auditLogsPaywall from "~/legacy-assets/images/illustrations/AuditLogsPaywall.svg?url";
import botCustomizationPaywall from "~/legacy-assets/images/illustrations/BotCustomizationPaywall.png";
import channelSharingPaywallImage from "~/legacy-assets/images/illustrations/ChannelSharingPaywall.svg?url";
import channelsPaywallImage from "~/legacy-assets/images/illustrations/ChannelsPaywall.png";
import clickupPaywall from "~/legacy-assets/images/illustrations/ClickupPaywall.svg?url";
import commentsPaywallImage from "~/legacy-assets/images/illustrations/CommentsPaywall.png";
import delegatedAccessPaywall from "~/legacy-assets/images/illustrations/DelegatedAccessPaywall.svg?url";
import embedsPaywallImage from "~/legacy-assets/images/illustrations/EmbedsPaywall.png";
import guidanceImage from "~/legacy-assets/images/illustrations/EmployeeGuidance.svg?url";
import guestUsersPaywall from "~/legacy-assets/images/illustrations/GuestUsersPaywall.svg?url";
import hubspotPaywallImage from "~/legacy-assets/images/illustrations/HubspotPaywall.svg?url";
import linearPaywall from "~/legacy-assets/images/illustrations/LinearPaywall.svg?url";
import meetingCostPaywall from "~/legacy-assets/images/illustrations/MeetingCostPaywall.svg?url";
// eslint-disable-next-line max-len
import meetingGuidelinesAnalyticsPaywall from "~/legacy-assets/images/illustrations/MeetingGuidelinesAnalyticsPaywall.svg?url";
import meetingGuidelinesPaywall from "~/legacy-assets/images/illustrations/MeetingGuidelinesPaywall.svg?url";
import meetingRecorderPaywall from "~/legacy-assets/images/illustrations/MeetingRecorderPaywall.png";
// eslint-disable-next-line max-len
import meetingRecordingUploadsPaywallImage from "~/legacy-assets/images/illustrations/MeetingRecordingUploadsPaywall.svg?url";
import microsoftTodoPaywall from "~/legacy-assets/images/illustrations/MicrosoftTodoPaywall.svg?url";
import mondayPaywall from "~/legacy-assets/images/illustrations/MondayPaywall.svg?url";
import multipleAssigneesPaywallImage from "~/legacy-assets/images/illustrations/MultipleAssigneesPaywall.svg?url";
import myTeamPseudoChannelPaywallImage from "~/legacy-assets/images/illustrations/MyTeamPseudoChannelPaywall.png";
import noteHistoryPaywall from "~/legacy-assets/images/illustrations/NoteHistoryPaywall.svg?url";
import oktaPaywallImage from "~/legacy-assets/images/illustrations/OktaPaywall.svg?url";
import publicLinksPaywallImage from "~/legacy-assets/images/illustrations/PublicLinksPaywall.svg?url";
import recapCommentsPaywallImage from "~/legacy-assets/images/illustrations/RecapCommentsPaywall.svg?url";
import recapPrivateCommentsPaywallImage from "~/legacy-assets/images/illustrations/RecapPrivateCommentsPaywall.svg?url";
import recapTemplatesPaywall from "~/legacy-assets/images/illustrations/RecapTemplatesPaywall.png";
import redactionPaywallImage from "~/legacy-assets/images/illustrations/RedactionPaywall.svg?url";
import salesforcePaywallImage from "~/legacy-assets/images/illustrations/SalesforcePaywall.svg?url";
import promptHeadingsPaywall from "~/legacy-assets/images/illustrations/SectionsPaywall.svg?url";
import smartTrackersPaywall from "~/legacy-assets/images/illustrations/SmartTrackersPaywall.svg?url";
import {
	default as objectivesPaywallImage,
	default as teamsPaywallImage,
} from "~/legacy-assets/images/illustrations/TeamsObjectivesPaywall.svg?url";
import templateImage from "~/legacy-assets/images/illustrations/templates-paywall.svg?url";
import threeSixtyPaywallImage from "~/legacy-assets/images/illustrations/ThreeSixtyPaywall.svg?url";
import trelloPaywall from "~/legacy-assets/images/illustrations/TrelloPaywall.svg?url";
import zapierPaywall from "~/legacy-assets/images/illustrations/zapier-paywall-illustration.png";
import duedatesVideo from "~/legacy-assets/videos/duedates.mp4";
import feedbackVideo from "~/legacy-assets/videos/feedback.mp4";
import guidelinesDrilldownVideo from "~/legacy-assets/videos/guidelinesDrilldown.mp4";
import jiraIntegrationVideo from "~/legacy-assets/videos/jiraIntegration.mp4";
import meetingAutomationsVideo from "~/legacy-assets/videos/meetingAutomations.mp4";
import meetingFeedbackVideo from "~/legacy-assets/videos/meetingFeedback.mp4";
import sharingVideo from "~/legacy-assets/videos/sharing.mp4";
import versioningVideo from "~/legacy-assets/videos/versioning.mp4";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import { environment } from "~/models";
import type { ValueOf } from "~/types";
import Button, { type ButtonProps } from "~/ui/button";

import { AppContext } from "../common";

import type { SubscriptionState } from "~/__generated__/UpgradeLink_viewer.graphql";
import type { utilsDummyQuery$data, utilsDummyQuery$variables } from "~/__generated__/utilsDummyQuery.graphql";
import type { utilsHubspotInfoQuery as HubspotQueryType } from "~/__generated__/utilsHubspotInfoQuery.graphql";

import styles from "./css/billingUtils.module.css";

const PRO_PLAN_HELP_ARTICLE_ID = "3623087";
const SOLOPRENEUR_PLAN_HELP_ARTICLE_ID = "9828838";
const BUSINESS_PLAN_HELP_ARTICLE_ID = "6084762";
const ENTERPRISE_PLAN_HELP_ARTICLE_ID = "4252549";
const FEEDBACK_ADDON_HELP_ARTICLE_ID = "3459649";
const OBJECTIVES_ADDON_HELP_ARTICLE_ID = "5647913";
const MEETING_RECORDER_HELP_ARTICLE_ID = "7995695";
export const TRIAL_END_HELP_ARTICLE_ID = "4088850";
export const INTEGRATIONS_HELP_ARTICLE_ID = "4367425";

export const $USD = (n: number, minimumFractionDigits = 2) =>
	n.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: minimumFractionDigits,
	});

export const $USDWhole = (n: number) => $USD(n).slice(0, -3);

export const billingStore = store.namespace("billing");

const hubspotInfoQuery = graphql`
	query utilsHubspotInfoQuery {
		viewer {
			account {
				benchmarkCompanySize
				hubspotOwner {
					email
				}
				hubspotSegment
			}
		}
	}
`;

const REVENUE_HERO_BASE_URL = "https://fellow.schedulehero.io";

export async function bookSalesCall(queryParams: Record<string, string> = {}) {
	const result = await fetchQuery<HubspotQueryType>(
		environment,
		hubspotInfoQuery,
		{},
		{ fetchPolicy: "store-or-network" },
	).toPromise();

	const { hubspotOwner, benchmarkCompanySize: benchmarkCompanySizeStr } = result?.viewer.account ?? {};
	const benchmarkCompanySize = parseInt(benchmarkCompanySizeStr ?? "0", 10);
	let meetingLink;

	// If we have a hubspot owner, we can use their email to generate a unique link.
	// Otherwise, use the universal inbound link that supports campaign link functionality.
	if (hubspotOwner && hubspotOwner.email) {
		const revenueHeroOwnerUsername = hubspotOwner.email.split("@")[0].toLowerCase().replace(/\./g, "-");
		meetingLink = `${REVENUE_HERO_BASE_URL}/meet/${revenueHeroOwnerUsername}/disco`;
	} else {
		meetingLink = `${REVENUE_HERO_BASE_URL}/inbound/fellow-schedule-meeting`;
	}

	const formParams = new URLSearchParams({
		/* eslint-disable camelcase */
		firstname: queryParams.first_name || "",
		lastname: queryParams.last_name || "",
		email: queryParams.email || "",
		employees: benchmarkCompanySize.toString() || "",
		utm_source: queryParams.utm_source || "prod_pw", // pw: paywall source
		utm_campaign: queryParams.utm_campaign || "",
		/* eslint-enable camelcase */
	}).toString();

	try {
		// Test if the meeting link is valid (The link for the owner might not be valid).
		// Fallback to the default link if the owner link is invalid.
		const response = await fetch(meetingLink, { method: "HEAD", redirect: "manual" });

		if (response.ok) {
			return window.open(`${meetingLink}?${formParams}`, "_blank", "noopener");
		} else {
			const fallbackLink = `${REVENUE_HERO_BASE_URL}/inbound/fellow-schedule-meeting`;
			return window.open(`${fallbackLink}?${formParams}`, "_blank", "noopener");
		}
	} catch (error) {
		// If there's an error with the fetch request, open the default link
		const fallbackLink = `${REVENUE_HERO_BASE_URL}/inbound/fellow-schedule-meeting`;
		return window.open(`${fallbackLink}?${formParams}`, "_blank", "noopener");
	}
}

export async function bookTrainingCall(queryParams: Record<string, string> = {}) {
	const result = await fetchQuery<HubspotQueryType>(
		environment,
		hubspotInfoQuery,
		{},
		{ fetchPolicy: "store-or-network" },
	).toPromise();
	const { benchmarkCompanySize } = result?.viewer.account ?? {};

	const formParams = new URLSearchParams({
		/* eslint-disable camelcase */
		firstname: queryParams.first_name || "",
		lastname: queryParams.last_name || "",
		email: queryParams.email || "",
		employees: benchmarkCompanySize ? benchmarkCompanySize : "",
		utm_source: queryParams.utm_source || "prod_ro", // ro: re-onboarding source
		utm_campaign: queryParams.utm_campaign || "reonboarding",
		/* eslint-enable camelcase */
	}).toString();

	const meetingLink = `${REVENUE_HERO_BASE_URL}/campaign/velocity-disco?${formParams}`;

	return window.open(meetingLink, "_blank", "noopener");
}

interface BookSalesCallButtonProps extends ButtonProps {
	feature: FeatureValues;
	analyticsProperties: Record<string, unknown>;
}

// TODO delete this button?
export function BookSalesCallButton({
	feature,
	children,
	analyticsProperties,
	...rest
}: BookSalesCallButtonProps): JSX.Element | null {
	const result = useLazyLoadQuery<HubspotQueryType>(hubspotInfoQuery, {}, { fetchPolicy: "store-and-network" });
	const { hubspotSegment } = result?.viewer.account ?? {};
	const { user } = React.useContext(AppContext);

	const queryParams = React.useMemo(
		() => ({
			/* eslint-disable camelcase */
			first_name: user?.firstName ?? "",
			last_name: user?.lastName ?? "",
			email: user?.email ?? "",
			utm_campaign: feature,
			/* eslint-enable camelcase */
		}),
		[feature, user?.email, user?.firstName, user?.lastName],
	);

	const onBookSalesCall = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", {
			...analyticsProperties,
			hubspotSegment: hubspotSegment,
			action: "book_sales_call",
		});
		bookSalesCall(queryParams);
	}, [analyticsProperties, hubspotSegment, queryParams]);

	return (
		<Button variant="billing" onClick={onBookSalesCall} {...rest}>
			{children}
		</Button>
	);
}

interface RequestDemoButtonProps extends ButtonProps {
	analyticsProperties: Record<string, unknown>;
}

export function RequestDemoButton({
	children,
	analyticsProperties,
	...rest
}: RequestDemoButtonProps): JSX.Element | null {
	const result = useLazyLoadQuery<HubspotQueryType>(hubspotInfoQuery, {}, { fetchPolicy: "store-and-network" });
	const { hubspotSegment } = result?.viewer.account ?? {};
	const { user } = React.useContext(AppContext);

	const queryParams = React.useMemo(
		() => ({
			/* eslint-disable camelcase */
			first_name: user?.firstName ?? "",
			last_name: user?.lastName ?? "",
			/* eslint-enable camelcase */
			email: user?.email ?? "",
		}),
		[user?.email, user?.firstName, user?.lastName],
	);

	const onRequestDemo = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", {
			...analyticsProperties,
			hubspotSegment: hubspotSegment,
			action: "book_sales_call",
		});
		bookSalesCall(queryParams);
	}, [analyticsProperties, hubspotSegment, queryParams]);

	return (
		<Button onClick={onRequestDemo} {...rest}>
			{children}
		</Button>
	);
}

export async function chatWithSales(message = "Hi! I'm interested in upgrading my workspace.") {
	if (window.Intercom) {
		return window.Intercom("showNewMessage", message);
	}

	const result = await fetchQuery<HubspotQueryType>(environment, hubspotInfoQuery, {}).toPromise();
	const { hubspotOwner } = result?.viewer.account ?? {};

	window.open(`mailto:${hubspotOwner?.email ?? "sales@fellow.co"}`, "_blank", "noopener");
}

export function getNextBillingCycleAnchorDate(billingCycleAnchor: string | null): Date | null {
	if (!billingCycleAnchor) return null;

	let nextCycleAnchor = new Date(billingCycleAnchor);

	// Get the current month and year
	const currentMonth = new Date().getMonth();
	const currentYear = new Date().getFullYear();

	// If the next occurrence is in the current or future month, return it
	if (nextCycleAnchor >= new Date()) return nextCycleAnchor;

	// If not, calculate the next occurrence in the future
	nextCycleAnchor.setFullYear(currentYear, currentMonth, nextCycleAnchor.getDate());

	// If the next occurrence is still in the past, move to the next month
	if (nextCycleAnchor <= new Date()) {
		nextCycleAnchor = addMonths(nextCycleAnchor, 1);
	}

	return nextCycleAnchor;
}

export function shouldShowUpgradeLink({
	subscriptionState,
	subscriptionStartDate,
	productFamily,
	hasDismissedElement,
}: {
	subscriptionState: SubscriptionState;
	productFamily: ProductFamilyKey;
	hasDismissedElement: boolean;
	subscriptionStartDate: string | null;
}) {
	// If workspace is trialing or is on Free plan => show upgrade link
	if (subscriptionState !== "SUBSCRIBED") return true;

	// If the user is not on the Pro/Team plan => hide upgrade link
	if (productFamily !== "PRO") return false;

	// If the user has already dismissed the upgrade link => hide upgrade link
	if (hasDismissedElement) return false;

	// If the user has not started their subscription => hide upgrade link
	if (!subscriptionStartDate) return false;

	// If the subscription start date is more than 14 days ago => hide upgrade link
	return differenceInDays(new Date(), new Date(subscriptionStartDate)) < 14;
}

// Dummy fragment so we can import the BillingFeature type
graphql`
	query utilsDummyQuery($feature: BillingFeature!) {
		viewer {
			billingAccount {
				hasAccessToFeature(feature: $feature)
				product {
					family
				}
			}
		}
	}
`;

export type FeatureValues = utilsDummyQuery$variables["feature"];
export type BillingFeatureAccessType = { [key in FeatureValues]: boolean };
export type ProductFamilyKey = utilsDummyQuery$data["viewer"]["billingAccount"]["product"]["family"];

function helpArticleURL(helpArticleId: string) {
	return `https://help.fellow.app/en/articles/${helpArticleId}`;
}

export const ProductFamilies: Record<
	ProductFamilyKey,
	{ helpArticleId: string; helpArticle: string; icon: IconDefinition; isAddon: boolean }
> = {
	FREE: {
		helpArticleId: "5780381", // Placeholder article ID
		helpArticle: helpArticleURL("5780381"),
		icon: faUser,
		isAddon: false,
	},
	PRO: {
		helpArticleId: PRO_PLAN_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(PRO_PLAN_HELP_ARTICLE_ID),
		icon: faCrown,
		isAddon: false,
	},
	BUSINESS: {
		helpArticleId: BUSINESS_PLAN_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(BUSINESS_PLAN_HELP_ARTICLE_ID),
		icon: faBuilding,
		isAddon: false,
	},
	ENTERPRISE: {
		helpArticleId: ENTERPRISE_PLAN_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(ENTERPRISE_PLAN_HELP_ARTICLE_ID),
		icon: faCity,
		isAddon: false,
	},
	ENTERPRISE_PLUS: {
		helpArticleId: ENTERPRISE_PLAN_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(ENTERPRISE_PLAN_HELP_ARTICLE_ID),
		icon: faCity,
		isAddon: false,
	},
	SOLOPRENEUR: {
		helpArticleId: SOLOPRENEUR_PLAN_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(SOLOPRENEUR_PLAN_HELP_ARTICLE_ID),
		icon: faHeadphones,
		isAddon: false,
	},
	FEEDBACK: {
		helpArticleId: FEEDBACK_ADDON_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(FEEDBACK_ADDON_HELP_ARTICLE_ID),
		icon: faThumbsUp,
		isAddon: true,
	},
	OBJECTIVES: {
		helpArticleId: OBJECTIVES_ADDON_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(OBJECTIVES_ADDON_HELP_ARTICLE_ID),
		icon: faBullseyeArrow,
		isAddon: true,
	},
	MEETING_RECORDER: {
		helpArticleId: MEETING_RECORDER_HELP_ARTICLE_ID,
		helpArticle: helpArticleURL(MEETING_RECORDER_HELP_ARTICLE_ID),
		icon: faSparkles,
		isAddon: true,
	},
};

/* eslint-disable camelcase */
export const Products = {
	fellow_free: {
		externalId: "fellow_free",
		name: "Free",
		family: "FREE",
		...ProductFamilies["FREE"],
	},
	fellow_pro: {
		externalId: "fellow_pro",
		name: "Legacy Pro",
		family: "PRO",
		...ProductFamilies["PRO"],
	},
	fellow_pro_ai: {
		externalId: "fellow_pro_ai",
		name: "Pro",
		family: "PRO",
		...ProductFamilies["PRO"],
	},
	fellow_business: {
		externalId: "fellow_business",
		name: "Legacy Business",
		family: "BUSINESS",
		...ProductFamilies["BUSINESS"],
	},
	fellow_business_ai: {
		externalId: "fellow_business_ai",
		name: "Business",
		family: "BUSINESS",
		...ProductFamilies["BUSINESS"],
	},
	fellow_enterprise: {
		externalId: "fellow_enterprise",
		name: "Legacy Enterprise",
		family: "ENTERPRISE",
		...ProductFamilies["ENTERPRISE"],
	},
	fellow_enterprise_ai: {
		externalId: "fellow_enterprise_ai",
		name: "Enterprise",
		family: "ENTERPRISE",
		...ProductFamilies["ENTERPRISE"],
	},
	fellow_unlimited: {
		externalId: "fellow_unlimited",
		name: "Legacy Enterprise",
		family: "ENTERPRISE_PLUS",
		...ProductFamilies["ENTERPRISE_PLUS"],
	},
	fellow_unlimited_ai: {
		externalId: "fellow_unlimited_ai",
		name: "Enterprise",
		family: "ENTERPRISE_PLUS",
		...ProductFamilies["ENTERPRISE_PLUS"],
	},
	addon_feedback: {
		externalId: "addon_feedback",
		name: "Feedback",
		family: "FEEDBACK",
		...ProductFamilies["FEEDBACK"],
	},
	addon_objectives: {
		externalId: "addon_objectives",
		name: "Teams & Objectives",
		family: "OBJECTIVES",
		...ProductFamilies["OBJECTIVES"],
	},
	addon_meeting_recorder: {
		externalId: "addon_meeting_recorder",
		name: "Meeting Recorder Unlimited",
		family: "MEETING_RECORDER",
		...ProductFamilies["MEETING_RECORDER"],
	},
} as const;
/* eslint-enable camelcase */

/** @deprecated Use `UpgradeIcon` in BillingIdentity.tsx instead. -Wal */
export const billingUpgradeIconDef = faArrowAltCircleUp;

export type ProductKeys = keyof typeof Products;
export type ProductValues = ValueOf<typeof Products>;
export type ProductExternalIDs = keyof typeof Products;

/* eslint-disable camelcase */
export const FeatureInfo: Record<
	FeatureValues,
	{
		title: string;
		boldText: string;
		description: string;
		product: {
			externalId: ProductKeys;
			name: string;
			family: ProductFamilyKey;
			helpArticleId: string;
			helpArticle: string;
			icon: IconDefinition;
			isAddon: boolean;
		};
		videoURL?: string;
		withBorder?: boolean;
		image?: React.ReactElement;
	}
> = {
	//------------------//
	//   FELLOW FREE    //
	//------------------//
	google_workspace: {
		title: "Google Workspace",
		boldText: "Sync users from Google Workspace.",
		description: "Sync all users or select organizational units from Google Workspace into Fellow.",
		product: Products["fellow_free"],
	},
	office_workspace: {
		title: "Microsoft Office 365",
		boldText: "Sync users from Microsoft Office 365.",
		description: "Sync all users or select groups from Microsoft Office 365 Workspace into Fellow.",
		product: Products["fellow_free"],
	},

	//------------------//
	//   PRO PAYWALLS  //
	//------------------//
	versioning: {
		title: "Version history",
		boldText: "Track all changes.",
		description: "Go back in time and restore previous versions of your notes when you upgrade.",
		videoURL: versioningVideo,
		product: Products["fellow_pro_ai"],
	},
	meeting_automations: {
		title: "Meeting Automations",
		boldText: "Automate common meeting habits.",
		description:
			// eslint-disable-next-line max-len
			"Turn on workflows to save you time, remind of upcoming meetings and circulate notes after the meeting has ended.",
		videoURL: meetingAutomationsVideo,
		product: Products["fellow_pro_ai"],
	},
	duedates: {
		title: "Due dates",
		boldText: "Keep your team on track.",
		description: "Assign due dates to action items and get reminders for upcoming action items when you upgrade.",
		videoURL: duedatesVideo,
		product: Products["fellow_pro_ai"],
	},
	jira_integration: {
		title: "Jira integration",
		boldText: "Import Jira issues into Fellow.",
		// eslint-disable-next-line max-len
		description: "Sync with Jira and get real-time status updates from Jira tickets when you upgrade.",
		videoURL: jiraIntegrationVideo,
		product: Products["fellow_pro_ai"],
	},
	clickup_integration: {
		title: "Clickup",
		boldText: "Connect Fellow with Clickup.",
		description: "Sync your action items with Clickup when you upgrade.",
		image: <img src={clickupPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	trello_integration: {
		title: "Trello",
		boldText: "Connect Fellow with Trello.",
		description: "Sync your action items with Trello when you upgrade.",
		image: <img src={trelloPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	monday_integration: {
		title: "monday.com",
		boldText: "Connect Fellow with monday.com",
		description: "Sync your action items with monday.com when you upgrade.",
		image: <img src={mondayPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	linear_integration: {
		title: "Linear",
		boldText: "Connect Fellow with Linear",
		description: "Sync your action items with Linear when you upgrade.",
		image: <img src={linearPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	microsoft_todo_integration: {
		title: "Microsoft To Do",
		boldText: "Connect Fellow with Microsoft To Do",
		description: "Sync your action items with Microsoft To Do when you upgrade.",
		image: <img src={microsoftTodoPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	control_inviting: {
		title: "Limiting invites",
		boldText: "Take control of invites.",
		description: "Restrict the ability for users to invite members to join the workspace when you upgrade. ",
		product: Products["fellow_pro_ai"],
	},
	sharing: {
		title: "Shared note series",
		boldText: "Enable async collaboration.",
		description: "Communicate with your teammates on anything with unlimited sharing of notes when you upgrade.",
		videoURL: sharingVideo,
		product: Products["fellow_pro_ai"],
	},
	sharing_channels: {
		title: "Channel Sharing",
		boldText: "",
		description: "Share Meeting Notes and Recaps with teammates and Channels.",
		image: <img src={channelSharingPaywallImage} />,
		product: Products["fellow_pro_ai"],
	},
	history: {
		title: "Note history",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Go back in time and view notes and AI notes that were created over 15 days ago. Upgrade and get unlimited access to your entire history and more.",
		image: <img src={noteHistoryPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	zapier_integration: {
		title: "Zapier integration",
		boldText: "Connect with 3,000+ apps.",
		description:
			// eslint-disable-next-line max-len
			"Integrate your action items and meeting notes with your favorite apps using Zapier when you upgrade.",
		image: <img src={zapierPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	asana_integration: {
		title: "Asana integration",
		boldText: "Connect Fellow with Asana.",
		description: "Sync your action items with Asana when you upgrade.",
		product: Products["fellow_pro_ai"],
	},
	guest_users: {
		title: "Guest users",
		boldText: "Collaborate with external attendees.",
		description:
			// eslint-disable-next-line max-len
			"You only have access to collaborate with internal teammates in your workspace. Get access to collaborate with guest users when you upgrade.",
		product: Products["fellow_pro_ai"],
		image: <img src={guestUsersPaywall} />,
	},
	comments: {
		title: "Comments",
		boldText: "Facilitate discussion.",
		description: "Enable commenting on any type of content in the note when you upgrade.",
		product: Products["fellow_pro_ai"],
		image: <img src={commentsPaywallImage} style={{ maxWidth: "100%", display: "block" }} />,
	},
	public_links: {
		title: "Public links",
		boldText: "Enable public access to specific notes.",
		description: "Share a view-only version of your notes with public links when you upgrade.",
		product: Products["fellow_pro_ai"],
		image: <img src={publicLinksPaywallImage} />,
	},
	multiple_assignees: {
		title: "Multiple assignees",
		boldText: "Keep your team on track.",
		description:
			// eslint-disable-next-line max-len
			"Create action items with shared ownership or assign multiple individuals to complete the same task when you upgrade.",
		product: Products["fellow_pro_ai"],
		image: <img src={multipleAssigneesPaywallImage} style={{ width: "100%" }} />,
	},
	embeds: {
		title: "Embedded media",
		boldText: "Enrich your meeting note content.",
		description:
			// eslint-disable-next-line max-len
			"Insert media directly in your note from YouTube, Figma, Miro, Spotify and others when you upgrade.",
		product: Products["fellow_pro_ai"],
		image: <img src={embedsPaywallImage} style={{ maxWidth: "100%", display: "block" }} />,
	},
	prompt_headings: {
		title: "Sections",
		boldText: "",
		description: "Help your team prep for meetings by requiring meeting participants to add to sections of the agenda.",
		product: Products["fellow_pro_ai"],
		image: <img src={promptHeadingsPaywall} />,
	},
	// eslint-disable-next-line camelcase
	users_bulk_csv_import: {
		title: "Import users via CSV",
		boldText: "",
		description: "",
		product: Products["fellow_pro_ai"],
	},
	notes_integration_export: {
		title: "Exporting notes",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Export meeting notes and series to third party applications such as Google Drive, Confluence, and Notion when you upgrade.",
		product: Products["fellow_pro_ai"],
	},
	meeting_recorder: {
		title: "Note Taker",
		boldText: "Bring the power of AI to your meetings.",
		description: "Get unlimited meeting recordings, transcripts and AI-generated summaries.",
		image: <img src={meetingRecorderPaywall} />,
		product: Products["addon_meeting_recorder"],
	},
	meeting_cost: {
		title: "Meeting cost",
		boldText: "",
		description: `Ensure all your meetings are high value by getting your team to think consciously about the cost of
		a meeting. Set a meeting cost for all users of your workspace in dollars or people hours when you upgrade.`,
		image: <img src={meetingCostPaywall} />,
		product: Products["fellow_pro_ai"],
	},
	meeting_briefs: {
		// FIXME: update these once we know what the paywall should look like
		title: "AI Meeting Brief",
		boldText: "",
		description: "",
		image: <div />,
		product: Products["fellow_pro_ai"],
	},
	meeting_recording_monthly_upload_credits: {
		title: "Audio and video uploads",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Upload any audio or video file — whether it's a past meeting, a recorded conversation, or anything else — and Fellow will automatically transcribe and create AI notes in minutes.",
		product: Products["fellow_pro_ai"],
		image: <img src={meetingRecordingUploadsPaywallImage} />,
	},
	//----------------------//
	//   BUSINESS PAYWALLS  //
	//----------------------//
	meeting_feedback: {
		title: "Meeting Feedback",
		boldText: "Improve your meetings.",
		description: "Capture meeting feedback directly from your meeting notes when you upgrade.",
		videoURL: meetingFeedbackVideo,
		product: Products["fellow_business_ai"],
	},
	templates: {
		title: "Workspace-wide templates",
		boldText: "Consistent meeting structure.",
		description: "Share custom templates with all members across the workspace when you upgrade.",
		image: <img src={templateImage} />,
		product: Products["fellow_business_ai"],
	},
	employee_guidance: {
		title: "Employee guidance",
		boldText: "Consistent talking points.",
		description:
			// eslint-disable-next-line max-len
			"Create suggested talking points for teams to add and discuss during upcoming 1-on-1s when you upgrade.",
		product: Products["fellow_business_ai"],
		image: <img src={guidanceImage} />,
	},
	note_locking: {
		title: "Note locking",
		boldText: "Keep an accurate record.",
		description:
			// eslint-disable-next-line max-len
			"Enable locking of meeting notes to prevent users from editing past notes when you upgrade.",
		product: Products["fellow_business_ai"],
	},
	salesforce_integration: {
		title: "Salesforce integration",
		boldText: "Connect Fellow with Salesforce.",
		description: "Sync your Notes to Salesforce when you upgrade.",
		image: <img src={salesforcePaywallImage} />,
		product: Products["fellow_business_ai"],
	},
	hubspot_integration: {
		title: "HubSpot integration",
		boldText: "Connect Fellow with HubSpot.",
		description: "Sync your Notes to HubSpot when you upgrade.",
		image: <img src={hubspotPaywallImage} />,
		product: Products["fellow_business_ai"],
	},
	recordings_library: {
		title: "Recordings library",
		boldText: "All your meeting recordings in one place.",
		description: "Stay up to date by quickly accessing all your meeting recordings and clips.",
		product: Products["fellow_business_ai"],
	},
	recap_comments: {
		title: "Comments",
		boldText: "Facilitate discussion.",
		description:
			// eslint-disable-next-line max-len
			"Leave comments on transcripts to coach, clarify, and keep discussions moving. Upgrade to start the conversation.",
		product: Products["fellow_pro_ai"],
		image: <img src={recapCommentsPaywallImage} />,
	},
	recap_private_comments: {
		title: "Private comments",
		boldText: "Keep comments private.",
		description: "Send comments directly to a teammate for their eyes only. Upgrade to comment privately.",
		product: Products["fellow_enterprise_ai"],
		image: <img src={recapPrivateCommentsPaywallImage} />,
	},
	channels: {
		title: "Channels",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Channels help you keep your recordings organized and easily shareable. Group multiple recordings and clips into one place.",
		image: <img src={channelsPaywallImage} />,
		product: Products["fellow_business_ai"],
	},
	recap_templates: {
		title: "Sales recap templates",
		boldText: "",
		description: "Automatically extract notes and sales insights from your customer calls when you upgrade.",
		image: <img src={recapTemplatesPaywall} />,
		product: Products["fellow_business_ai"],
	},
	keyword_smart_trackers: {
		title: "Trackers",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Set up keywords like your competitors' names, or concepts like Objection Handling, and track when they're mentioned in your meetings.",
		image: <img src={smartTrackersPaywall} />,
		product: Products["fellow_business_ai"],
	},
	my_team_pseudo_channel: {
		title: "My team",
		boldText: "",
		description:
			"Get meeting insights for your team to uncover patterns, improve collaboration, and coach more effectively.",
		image: <img src={myTeamPseudoChannelPaywallImage} />,
		product: Products["fellow_business_ai"],
	},
	custom_vocabulary: {
		title: "Custom vocabulary",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Enhance transcription accuracy by adding custom words and spellings to your personalized dictionary. Upgrade to unlock this feature and ensure your transcripts capture every detail perfectly.",
		product: Products["fellow_pro_ai"],
	},
	//------------------------//
	//   ENTERPRISE PAYWALLS  //
	//------------------------//
	analytics: {
		title: "Analytics",
		boldText: "Discover meaningful team insights.",
		description:
			// eslint-disable-next-line max-len
			"View analytics for all members on 1-on-1s, meetings, feedback, action items and more when you upgrade.",
		product: Products["fellow_enterprise_ai"],
		image: <img src={analyticsImage} />,
	},
	sso: {
		title: "SSO integration",
		boldText: "Connect Fellow with SSO providers.",
		description: "Provision Fellow users using your company's SSO user-base. Allow users to log in via external SSO.",
		image: <img src={oktaPaywallImage} />,
		product: Products["fellow_enterprise_ai"],
	},
	delegated_access: {
		title: "Delegated Access",
		boldText: "Grant users access to your account.",
		description: "Allow users to add teammates as delegates.",
		image: <img src={delegatedAccessPaywall} />,
		product: Products["fellow_enterprise_ai"],
	},
	audit_log: {
		title: "Audit Log",
		boldText: "Track your teams activity across your workspace and export your data.",
		description: "Get access to your audit log when you upgrade.",
		image: <img src={auditLogsPaywall} />,
		product: Products["fellow_enterprise_ai"],
	},
	meeting_guidelines: {
		title: "Meeting Guidelines",
		boldText: "",
		description: `Shape meeting habits across your organization with built-in Meeting Guidelines. Ensure every meeting
		is a valuable one with integrated guiding prompts in Google Calendar when you upgrade.`,
		image: <img src={meetingGuidelinesPaywall} />,
		product: Products["fellow_enterprise_ai"],
	},
	meeting_guidelines_analytics: {
		title: "Meeting Guidelines Analytics",
		boldText: "",
		description: `Get insights into how your workspace is measuring up against the meeting guidelines
		when you upgrade.`,
		image: <img src={meetingGuidelinesAnalyticsPaywall} />,
		product: Products["fellow_enterprise_ai"],
	},
	meeting_guidelines_drilldown: {
		title: "Meeting drilldowns",
		boldText: "",
		description: `Get detailed reports on all meetings happening in your workspace, including its cost, compliance
		against meeting guidelines, and more.`,
		videoURL: guidelinesDrilldownVideo,
		withBorder: true,
		product: Products["fellow_enterprise_ai"],
	},
	meeting_recording_auto_delete: {
		title: "Meeting Recording Auto Deletion",
		boldText: "Schedule automatic deletion of recordings and transcripts.",
		description: "Automatically delete meeting recordings after a set period of time.",
		product: Products["fellow_enterprise_ai"],
	},
	workspace_custom_auto_record_rules: {
		title: "Recording rules & restrictions",
		boldText: "",
		// eslint-disable-next-line max-len
		description: `Gain control over which meetings are restricted from recording and which are automatically recorded when you upgrade.`,
		product: Products["fellow_enterprise_ai"],
	},
	redaction: {
		title: "Redaction",
		boldText: "",
		// eslint-disable-next-line max-len
		description:
			"Redact sensitive topics from your meeting recordings and transcripts for enhanced security and privacy.",
		product: Products["fellow_enterprise_ai"],
		image: <img src={redactionPaywallImage} />,
	},
	concept_smart_trackers: {
		title: "Concept trackers",
		boldText: "",
		description:
			"Set up concepts like Objection Handling and Feature Request, and track when they're mentioned in your meetings.",
		image: <img src={smartTrackersPaywall} />,

		product: Products["fellow_enterprise_ai"],
	},
	bot_customization: {
		title: "Note Taker customization",
		boldText: "",
		description: "Upgrade to customize the name, avatar and initial message of the Note Taker.",
		image: <img src={botCustomizationPaywall} />,
		product: Products["fellow_enterprise_ai"],
	},
	salesforce_ai_field_updates: {
		title: "AI-Powered Salesforce Field Updates",
		boldText: "",
		description:
			"Keep your Salesforce records clean and up to date with AI-suggested field " +
			"updates—reducing admin work so your reps can focus on selling.",
		image: <img src={salesforcePaywallImage} />,
		product: Products["fellow_enterprise_ai"],
	},
	default_user_configurations: {
		title: "Default User Configurations",
		boldText: "",
		description:
			// eslint-disable-next-line max-len
			"Configure, and optionally enforce, the default recording and AI notes sharing settings for your workspace users.",
		product: Products["fellow_enterprise_ai"],
	},
	//-----------------------------//
	//   ENTERPRISE PLUS PAYWALLS  //
	//-----------------------------//
	feedback: {
		title: "Feedback",
		boldText: "Build a culture of continuous feedback.",
		description:
			// eslint-disable-next-line max-len
			"Give, receive or request feedback from your teammates on meetings or people or anything else when you upgrade.",
		videoURL: feedbackVideo,
		product: Products["fellow_unlimited_ai"],
	},
	three_sixty_feedback: {
		title: "360 Feedback",
		boldText: "Build a culture of continuous feedback.",
		description: `Capture feedback about an employee from the people who work around them.
			Automate sending requests with our 360 Feedback builder.`,
		image: <img src={threeSixtyPaywallImage} />,
		product: Products["fellow_unlimited_ai"],
	},
	objectives: {
		title: "Objectives and key results",
		boldText: "Drive performance, employee engagement and growth.",
		description: "Track progress towards your company, team, and individual objectives.",
		image: <img src={objectivesPaywallImage} />,
		product: Products["fellow_unlimited_ai"],
	},
	developer_api: {
		title: "Developer API",
		boldText: "Integrate Fellow with your tools.",
		description: "Access Fellow's data programmatically through our comprehensive REST API.",
		product: Products["fellow_pro_ai"],
	},
	teams: {
		title: "Teams and Objectives",
		boldText: "Drive performance, employee engagement and growth.",
		description: "Track, collaborate, and align on team and company objectives.",
		image: <img src={teamsPaywallImage} />,
		product: Products["fellow_unlimited_ai"],
	},
};
/* eslint-enable camelcase */

const FreeCheck = <FontAwesomeIcon icon={faCheck} color="#8E9CA3" />;
const ProCheck = <FontAwesomeIcon icon={faCheck} color={"var(--billingPro)"} />;
const SolopreneurCheck = <FontAwesomeIcon icon={faCheck} color={"var(--billingSolopreneur)"} />;
const BusinessCheck = <FontAwesomeIcon icon={faCheck} color={"var(--billingBusiness)"} />;
const EnterpriseCheck = <FontAwesomeIcon icon={faCheck} color={"var(--billingEnterprise)"} />;

export const FeatureList = [
	{
		heading: "Users",
	},
	{
		feature: "Maximum seats",
		free: "5",
		pro: "20",
		solopreneur: "1",
		business: "50",
		enterprise: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
	},
	{
		heading: "AI meeting assistant",
	},
	{
		feature: "AI meeting notes",
		free: "Limited",
		pro: "Limited",
		solopreneur: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		business: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		enterprise: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
	},
	{
		feature: "AI meeting recordings",
		free: "Limited",
		pro: "Limited",
		solopreneur: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		business: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		enterprise: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
	},
	{
		feature: "AI meeting summary",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI action items",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI meeting transcription",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI meeting agenda",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Ask Fellow (AI chatbot)",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Recording library",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Custom Recording Channels",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI pre-meeting briefs",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI recap templates",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Custom storage time",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Customize AI meeting assistant (avatar, display name, and initial message)",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Advanced recording & sharing permissions",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Redaction",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		heading: "AI meeting assistant for sales",
	},
	{
		feature: "AI suggestions for Salesforce",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI pre-meeting briefs for Sales",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "AI recap templates for Sales",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Trackers",
		tooltip: "Automatically identify specific keywords, or concepts across meetings in your organization",
		free: "",
		pro: "",
		solopreneur: "Keyword only",
		business: "Keyword only",
		enterprise: "Keyword & Concept",
	},
	{
		heading: "Integrations",
	},
	{
		feature: "Google Meet",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Google Calendar",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Google Drive",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Microsoft 365 Calendar",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "MS Teams",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Zoom",
		tooltip: "Bring your meeting notes and action items right within your Zoom meeting!",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Slack",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Loom",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Jira",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Asana",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Zapier",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "ClickUp",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Trello",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "monday.com",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Linear",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Microsoft To Do",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Salesforce",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "HubSpot",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Okta SSO",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "OneLogin",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Meeting productivity",
	},
	{
		feature: "Meeting notes history",
		free: "Limited",
		pro: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		solopreneur: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		business: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
		enterprise: (
			<>
				<FontAwesomeIcon icon={faInfinity} className={styles.valueIcon} />
				<span className={styles.valueText}>Unlimited</span>
			</>
		),
	},
	{
		feature: "Collaborative meeting agendas",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Private notes",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Public note links",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Note version history",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Embedded media",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Exporting notes",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Multi-calendar sync",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Feedback in meetings",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Note locking",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Workspace analytics",
		tooltip: "Graphs, trends, and stats on how your organization uses Fellow for 1:1s, meetings, and action items",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Collaboration",
	},
	{
		feature: "Shared notepads",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Due dates on action items",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Multiple assignees on action items",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "External (Guest) users",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Workspace-wide templates",
		tooltip: "Create and apply default templates to your One-on-Ones, Meetings, and Personal notes",
		free: "",
		pro: "",
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Meeting automations",
	},
	{
		feature: "Pre-meeting reminders",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "No agenda reminder & cancellation",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Post-meeting recaps",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Add to Section reminders",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Meeting policy",
	},
	{
		feature: "Meeting timer",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Meeting cost calculator",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Optimal attendees",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "No meeting day",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Short notice bookings",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Meeting purpose",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Finite recurring meetings",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Maximum 20h of meetings",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Security and privacy",
	},
	{
		feature: "GDPR compliance",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "SOC 2 type II compliance",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Admin role limits",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "New user controls",
		free: "",
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Single Sign On (Okta, One Login)",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "HRIS Sync",
		tooltip: "Google Workspace Sync, Microsoft 365 Sync, Bamboo HR and Workday",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Delegated access",
		tooltip: "Allow teammates to access and manage other accounts.",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Security and legal reviews",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Custom contracts",
		tooltip: "Contracts starting at $10,000",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		feature: "HIPAA / BAA compliance",
		tooltip: "Contracts starting at $10,000",
		free: "",
		pro: "",
		solopreneur: "",
		business: "",
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Product support",
	},
	{
		feature: "Onboarding program",
		free: "",
		pro: "",
		solopreneur: "",
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Dedicated account manager",
		free: "",
		pro: "",
		solopreneur: "",
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Payment by invoice",
		free: "",
		pro: "",
		solopreneur: "",
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Chat & email support",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		heading: "Apps & Extensions",
	},
	{
		feature: "macOS App",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Windows App",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "iOS App",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Android App",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Chrome Extension",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
	{
		feature: "Firefox Extension",
		free: FreeCheck,
		pro: ProCheck,
		solopreneur: SolopreneurCheck,
		business: BusinessCheck,
		enterprise: EnterpriseCheck,
	},
];

export const CA_PROVINCES = [
	{ value: "AB", text: "Alberta" },
	{ value: "BC", text: "British Columbia" },
	{ value: "MB", text: "Manitoba" },
	{ value: "NB", text: "New Brunswick" },
	{ value: "NL", text: "Newfoundland and Labrador" },
	{ value: "NT", text: "Northwest Territories" },
	{ value: "NS", text: "Nova Scotia" },
	{ value: "NU", text: "Nunavut" },
	{ value: "ON", text: "Ontario" },
	{ value: "PE", text: "Prince Edward Island" },
	{ value: "QC", text: "Quebec" },
	{ value: "SK", text: "Saskatchewan" },
	{ value: "YT", text: "Yukon" },
];

export const US_STATES = [
	{
		text: "Alabama",
		value: "AL",
	},
	{
		text: "Alaska",
		value: "AK",
	},
	{
		text: "Arizona",
		value: "AZ",
	},
	{
		text: "Arkansas",
		value: "AR",
	},
	{
		text: "California",
		value: "CA",
	},
	{
		text: "Colorado",
		value: "CO",
	},
	{
		text: "Connecticut",
		value: "CT",
	},
	{
		text: "Delaware",
		value: "DE",
	},
	{
		text: "District Of Columbia",
		value: "DC",
	},
	{
		text: "Federated States Of Micronesia",
		value: "FM",
	},
	{
		text: "Florida",
		value: "FL",
	},
	{
		text: "Georgia",
		value: "GA",
	},
	{
		text: "Guam",
		value: "GU",
	},
	{
		text: "Hawaii",
		value: "HI",
	},
	{
		text: "Idaho",
		value: "ID",
	},
	{
		text: "Illinois",
		value: "IL",
	},
	{
		text: "Indiana",
		value: "IN",
	},
	{
		text: "Iowa",
		value: "IA",
	},
	{
		text: "Kansas",
		value: "KS",
	},
	{
		text: "Kentucky",
		value: "KY",
	},
	{
		text: "Louisiana",
		value: "LA",
	},
	{
		text: "Maine",
		value: "ME",
	},
	{
		text: "Marshall Islands",
		value: "MH",
	},
	{
		text: "Maryland",
		value: "MD",
	},
	{
		text: "Massachusetts",
		value: "MA",
	},
	{
		text: "Michigan",
		value: "MI",
	},
	{
		text: "Minnesota",
		value: "MN",
	},
	{
		text: "Mississippi",
		value: "MS",
	},
	{
		text: "Missouri",
		value: "MO",
	},
	{
		text: "Montana",
		value: "MT",
	},
	{
		text: "Nebraska",
		value: "NE",
	},
	{
		text: "Nevada",
		value: "NV",
	},
	{
		text: "New Hampshire",
		value: "NH",
	},
	{
		text: "New Jersey",
		value: "NJ",
	},
	{
		text: "New Mexico",
		value: "NM",
	},
	{
		text: "New York",
		value: "NY",
	},
	{
		text: "North Carolina",
		value: "NC",
	},
	{
		text: "North Dakota",
		value: "ND",
	},
	{
		text: "Northern Mariana Islands",
		value: "MP",
	},
	{
		text: "Ohio",
		value: "OH",
	},
	{
		text: "Oklahoma",
		value: "OK",
	},
	{
		text: "Oregon",
		value: "OR",
	},
	{
		text: "Palau",
		value: "PW",
	},
	{
		text: "Pennsylvania",
		value: "PA",
	},
	{
		text: "Puerto Rico",
		value: "PR",
	},
	{
		text: "Rhode Island",
		value: "RI",
	},
	{
		text: "South Carolina",
		value: "SC",
	},
	{
		text: "South Dakota",
		value: "SD",
	},
	{
		text: "Tennessee",
		value: "TN",
	},
	{
		text: "Texas",
		value: "TX",
	},
	{
		text: "Utah",
		value: "UT",
	},
	{
		text: "Vermont",
		value: "VT",
	},
	{
		text: "Virgin Islands",
		value: "VI",
	},
	{
		text: "Virginia",
		value: "VA",
	},
	{
		text: "Washington",
		value: "WA",
	},
	{
		text: "West Virginia",
		value: "WV",
	},
	{
		text: "Wisconsin",
		value: "WI",
	},
	{
		text: "Wyoming",
		value: "WY",
	},
];
